import { faFaceSmile } from '@fortawesome/pro-solid-svg-icons';
import {
  StrategyConfig,
  StrategyValidationContext,
  CreateStrategyPayload,
  UpdateStrategyPayload,
} from '../types';
import FreshFacesConfiguration from '../../Admin/AddEditStrategy/components/StrategyTypes/FreshFacesConfiguration';

/**
 * Helper to extract ID from code_set or filter_set which can be an object or ID
 */
const extractId = (value: any): string | number | undefined => {
  if (typeof value === 'object' && value !== null) {
    return value.id;
  }
  return value;
};

/**
 * Configuration for Fresh Faces strategy type
 *
 * Fresh Faces shows up-and-coming providers for specific codes in a region.
 * It uses code sets and filter sets rather than dynamic criteria.
 */
export const freshFacesConfig: StrategyConfig = {
  type: 'FRESH_FACES',
  label: 'Fresh Faces',
  description:
    'Show the up and comers for the codes you care about in your region.',
  icon: faFaceSmile,
  mode: 'target',
  disabled: false,
  disabledReason: 'Coming Soon',

  ConfigComponent: FreshFacesConfiguration,

  requiredFields: ['label', 'description', 'code_set', 'volume_type'],

  validateProgress: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) {
      return !!(
        context.strategyDetails?.criteria_template ||
        context.strategyDetails?.codeSetId ||
        context.strategyDetails?.mode
      );
    }
    // Only code_set and volume_type are required; filter_set is optional
    return !!(
      context.freshFacesData?.code_set && context.freshFacesData?.volume_type
    );
  },

  validateComplete: (context: StrategyValidationContext): boolean => {
    if (context.isEditMode || context.isCopyMode) return true;
    // Only code_set and volume_type are required; filter_set is optional
    return !!(
      context.freshFacesData?.code_set && context.freshFacesData?.volume_type
    );
  },

  serializeForCreate: (
    context: StrategyValidationContext
  ): CreateStrategyPayload => {
    // Extract team IDs - teams may be stored as objects or IDs
    const teams = context.formData.teams?.map((team: number | { id: number }) =>
      typeof team === 'object' ? team.id : team
    );

    return {
      label: context.formData.label,
      description: context.formData.description,
      type: 'FRESH_FACES',
      teams,
      refresh_event: context.formData.refresh_event,
      max_opportunities: context.formData.max_opportunities,
      code_set: extractId(context.freshFacesData?.code_set),
      filter_set: extractId(context.freshFacesData?.filter_set),
      volume_type: context.freshFacesData?.volume_type,
      provider_type: context.freshFacesData?.provider_type || 'hcp',
    };
  },

  serializeForUpdate: (
    context: StrategyValidationContext
  ): UpdateStrategyPayload => {
    return {
      label: context.formData.label,
      description: context.formData.description,
      refresh_event: context.formData.refresh_event,
      max_opportunities: context.formData.max_opportunities,
      code_set: extractId(context.freshFacesData?.code_set),
      filter_set: extractId(context.freshFacesData?.filter_set),
      volume_type: context.freshFacesData?.volume_type,
      provider_type: context.freshFacesData?.provider_type,
    };
  },

  features: {
    supportsRefresh: true,
    hasMaxOpportunities: true,
    usesCodeSet: true,
    usesFilterSet: true,
  },
};
